import React from "react";
import { Box, Paper, Grid, Typography, Autocomplete, TextField, Link } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { SxProps, Theme } from "@mui/material/styles";
import { OfficeWithPersona } from "@/models/goFurther";
import { FONT_SIZE, FONT_WEIGHT, TEXT_COLOR, BACKGROUND_COLOR, SECTION_CARD_SX, GO_FURTHER_PAGE_SX } from "../GoFurtherStyles";
import { CommonConstants } from "@/common/CommonConstants";

// --- Prop types ---

export type OfficeSelectorProps = {
  offices: OfficeWithPersona[];
  selectedOffice: OfficeWithPersona | null;
  onChangeOffice: (office: OfficeWithPersona | null) => void;
};

export type SimulationArrowPillProps = {
  children: React.ReactNode;
  ovalHeight: number;
  fontSize: string | number;
};

export type SectionGridItemCardProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  cardSx?: SxProps<Theme>;
  gridSx?: SxProps<Theme>;
  gridSpacing?: number;
};

export type SectionGridItemBannerCardProps = {
  /** カード上端に全幅で表示される帯（例:「他社様の成功事例紹介」） */
  banner: React.ReactNode;
  title?: React.ReactNode;
  /** タイトル下に注意書きとして表示される補足（例:「貴社のデータではありません」） */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  cardSx?: SxProps<Theme>;
  gridSx?: SxProps<Theme>;
  gridSpacing?: number;
};

export type SecondaryPanelProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

export type GoFurtherExternalLinkProps = {
  href: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

// --- Components ---

export const OfficeSelector: React.FC<OfficeSelectorProps> = ({ offices, selectedOffice, onChangeOffice }) => {
  if (offices.length <= 1) {
    return null;
  }

  return (
    <Box
      sx={(theme) => {
        const toolbarMixin = theme.mixins.toolbar as {
          minHeight?: number | string;
        };
        const toolbarMinHeight = toolbarMixin.minHeight ?? 56;

        return {
          position: "fixed",
          top: `calc(${typeof toolbarMinHeight === "number" ? `${toolbarMinHeight}px` : toolbarMinHeight} + ${theme.spacing(2)})`,
          right: 16,
          zIndex: theme.zIndex.drawer + 1,
          width: 384,
        };
      }}
    >
      <Paper
        sx={{
          p: 0,
          borderRadius: 4,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: 4 },
        }}
      >
        <Autocomplete
          options={offices}
          value={selectedOffice}
          getOptionLabel={(option) => `${option.displayName} (${option.id})`}
          renderOption={(props, option) => (
            <li {...props} style={{ ...(props.style || {}), fontSize: FONT_SIZE.body2 }}>
              {`${option.displayName} (${option.id})`}
            </li>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => {
            onChangeOffice(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={null}
              InputLabelProps={{
                ...params.InputLabelProps,
                sx: { fontSize: FONT_SIZE.body2 },
              }}
              inputProps={{
                ...params.inputProps,
                "aria-label": CommonConstants.CompanyOffice,
                style: { fontSize: FONT_SIZE.body2, paddingTop: "6px", paddingBottom: "6px", borderRadius: 4 },
              }}
            />
          )}
        />
      </Paper>
    </Box>
  );
};

export const SectionGridItemCard: React.FC<SectionGridItemCardProps> = ({
  title,
  children,
  cardSx,
  gridSx,
  gridSpacing,
}) => (
  <Paper sx={[SECTION_CARD_SX, ...(Array.isArray(cardSx) ? cardSx : cardSx ? [cardSx] : [])]}>
    <Box>{title}</Box>
    <Grid
      container
      spacing={gridSpacing}
      sx={[
        {
          mt: 5,
          "& > .MuiGrid-item + .MuiGrid-item": {
            mt: 5,
          },
        },
        ...(Array.isArray(gridSx) ? gridSx : gridSx ? [gridSx] : []),
      ]}
    >
      {children}
    </Grid>
  </Paper>
);

export const SectionGridItemBannerCard: React.FC<SectionGridItemBannerCardProps> = ({
  banner,
  title,
  subtitle,
  children,
  cardSx,
  gridSx,
  gridSpacing,
}) => (
  <Paper sx={[SECTION_CARD_SX, { p: 0, overflow: "hidden" }, ...(Array.isArray(cardSx) ? cardSx : cardSx ? [cardSx] : [])]}>
    <Box
      sx={{
        py: 2,
        px: 3,
        bgcolor: BACKGROUND_COLOR.GRAY,
        color: TEXT_COLOR.CONTRAST,
        fontWeight: FONT_WEIGHT.BOLD,
        fontSize: FONT_SIZE.headline5,
        textAlign: "center",
      }}
    >
      {banner}
    </Box>
    <Box sx={{ p: 5 }}>
      {title && (
        <Box>{title}</Box>
      )}
      {subtitle && (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, color: TEXT_COLOR.SECONDARY }}>
          <ErrorIcon sx={{ fontSize: FONT_SIZE.body1 }} />
          <Typography sx={{ fontSize: FONT_SIZE.body2, color: "inherit" }}>{subtitle}</Typography>
        </Box>
      )}
      <Grid
        container
        spacing={gridSpacing}
        sx={[
          {
            ...(title ? { mt: 5 } : {}),
            "& > .MuiGrid-item + .MuiGrid-item": {
              mt: 5,
            },
          },
          ...(Array.isArray(gridSx) ? gridSx : gridSx ? [gridSx] : []),
        ]}
      >
        {children}
      </Grid>
    </Box>
  </Paper>
);

export const GoFurtherExternalLink: React.FC<GoFurtherExternalLinkProps> = ({ href, children, sx }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={[
      { color: BACKGROUND_COLOR.INFO, textDecoration: "none" },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Link>
);

export const SecondaryPanel: React.FC<SecondaryPanelProps> = ({ children, sx }) => (
  <Box
    sx={[
      {
        bgcolor: BACKGROUND_COLOR.SECONDARY,
        p: 3,
        borderRadius: 2.5,
        border: `1px solid ${BACKGROUND_COLOR.INHERIT}`,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Box>
);

export const SimulationArrowPill: React.FC<SimulationArrowPillProps> = ({ children, ovalHeight, fontSize }) => {
  const topLineLength = Math.max(6, Math.round(ovalHeight * 0.24));
  const bottomLineLength = topLineLength;
  const arrowHeadHeight = Math.max(12, Math.round(ovalHeight * 0.34));
  const arrowHeadHalfWidth = Math.max(9, Math.round(ovalHeight * 0.24));

  return (
    <Box
      sx={{
        justifySelf: "center",
        px: 1,
        py: 2,
        height: ovalHeight,
        borderRadius: 99,
        bgcolor: BACKGROUND_COLOR.PRIMARY,
        color: TEXT_COLOR.CONTRAST,
        fontWeight: FONT_WEIGHT.BOLD,
        fontSize,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        isolation: "isolate",
        zIndex: 1,
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          top: -topLineLength,
          bottom: -bottomLineLength,
          width: 5,
          bgcolor: BACKGROUND_COLOR.PRIMARY,
          zIndex: -1,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          top: `calc(100% + ${bottomLineLength - 2}px)`,
          width: 0,
          height: 0,
          borderLeft: `${arrowHeadHalfWidth}px solid transparent`,
          borderRight: `${arrowHeadHalfWidth}px solid transparent`,
          borderTop: `${arrowHeadHeight}px solid ${BACKGROUND_COLOR.PRIMARY}`,
          zIndex: -1,
        },
        border: `1px solid ${BACKGROUND_COLOR.WHITE}`,
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          justifyContent: "center",
          alignItems: "baseline",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export type PreparingLayoutProps = {
  offices?: OfficeWithPersona[];
  selectedOffice?: OfficeWithPersona | null;
  onChangeOffice?: (office: OfficeWithPersona | null) => void;
};

export const PreparingLayout: React.FC<PreparingLayoutProps> = ({
  offices = [],
  selectedOffice = null,
  onChangeOffice = () => { },
}) => (
  <Box
    sx={(theme) => {
      const toolbarMinHeight = theme.mixins.toolbar as {
        minHeight?: number | string;
        [key: string]: unknown;
      };
      const smQuery = theme.breakpoints.up("sm");
      const smToolbar = toolbarMinHeight[smQuery] as { minHeight?: number | string } | undefined;
      const baseMinHeight = toolbarMinHeight.minHeight ?? 56;
      const smMinHeight = smToolbar?.minHeight ?? 64;
      const reservedForFooter = `calc(${theme.spacing(8)} + 24px)`;

      return {
        ...GO_FURTHER_PAGE_SX,
        position: "relative",
        height: `calc(100dvh - ${typeof baseMinHeight === "number" ? `${baseMinHeight}px` : baseMinHeight} - ${reservedForFooter})`,
        width: "100%",
        minWidth: 0,
        bgcolor: BACKGROUND_COLOR.INHERIT,
        color: TEXT_COLOR.PRIMARY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        [smQuery]: {
          height: `calc(100dvh - ${typeof smMinHeight === "number" ? `${smMinHeight}px` : smMinHeight} - ${reservedForFooter})`,
        },
      };
    }}
  >
    {/* Office selector */}
    <OfficeSelector offices={offices} selectedOffice={selectedOffice} onChangeOffice={onChangeOffice} />

    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
      <Box
        component="img"
        src="/charging_spot.png"
        alt="Charging spot"
        sx={{ width: "auto", height: "auto", maxWidth: 180, maxHeight: 140, objectFit: "contain" }}
      />
      <Typography sx={{ mt: 5, fontWeight: FONT_WEIGHT.BOLD, fontSize: FONT_SIZE.headline5, color: TEXT_COLOR.DISABLED }}>
        ただいま準備中です
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: TEXT_COLOR.SECONDARY,
          fontSize: FONT_SIZE.body2,
        }}
      >
        EVの収益最大化や更なる活用に向けたご要望などがございましたら
        <br />
        <Link href="mailto:gx_contact@goinc.jp" underline="none" sx={{ color: "inherit" }}>
          gx_contact@goinc.jp
        </Link>
        までお問い合わせください
      </Typography>
    </Box>
  </Box>
);
